import type { WidgetNodeProps } from "@/pages/canvas/types/canvasNodeData";
import type { WidgetPreset } from "../types";

/**
 * The sheet's drawn recipe (tray-stickers.jsx `Sticker` / `Badge`): a
 * `-light` spectrum field, an `--ink` line, `--f-sans` type, on a 96 × 64
 * plate, mode-constant because it is a preview of an object the canvas
 * stores. Built once and parameterised by word and hue rather than baked
 * into eleven SVGs, so it stays on the tokens (ROUND3 §3, D39).
 */

const LINE = "var(--ink)";
const FIELD = "var(--paper)";
export const REVIEW_STICKER_W = 96;
export const REVIEW_STICKER_H = 64;

export type ReviewStickerKind = "badge" | "speech" | "thought" | "arrow";

export interface ReviewStickerData {
  kind: ReviewStickerKind;
  word?: string;
  /** A spectrum `-light` token for the badge's field. */
  field?: string;
  /** Type size; "Needs review" steps down once to fit the pill. */
  size?: number;
}

export function ReviewStickerArt({
  kind,
  word = "",
  field = FIELD,
  size = 13,
  title,
}: ReviewStickerData & { title: string }) {
  return (
    <svg
      viewBox={`0 0 ${REVIEW_STICKER_W} ${REVIEW_STICKER_H}`}
      style={{ fontFamily: "var(--f-sans)", display: "block" }}
      width="100%"
      height="100%"
      role="img"
      aria-label={title}
      focusable="false"
    >
      {kind === "badge" && (
        <>
          <rect
            x="5"
            y="19"
            width="86"
            height="26"
            rx="6"
            fill={field}
            stroke={LINE}
            strokeWidth="1.6"
          />
          <text
            x="48"
            y="36.4"
            textAnchor="middle"
            fontSize={size}
            fontWeight="600"
            fill={LINE}
          >
            {word}
          </text>
        </>
      )}
      {kind === "speech" && (
        <>
          <path
            d="M10 12h76a5 5 0 0 1 5 5v22a5 5 0 0 1-5 5H36l-12 10 2-10H10a5 5 0 0 1-5-5V17a5 5 0 0 1 5-5Z"
            fill={FIELD}
            stroke={LINE}
            strokeWidth="1.6"
            strokeLinejoin="round"
          />
          <path
            d="M16 24h50M16 33h32"
            stroke={LINE}
            strokeWidth="1.6"
            strokeLinecap="round"
            opacity="0.35"
          />
        </>
      )}
      {kind === "thought" && (
        <>
          <path
            d="M20 10h56a12 12 0 0 1 0 24H20a12 12 0 0 1 0-24Z"
            fill={FIELD}
            stroke={LINE}
            strokeWidth="1.6"
          />
          <circle
            cx="26"
            cy="43"
            r="5"
            fill={FIELD}
            stroke={LINE}
            strokeWidth="1.6"
          />
          <circle
            cx="17"
            cy="53"
            r="3"
            fill={FIELD}
            stroke={LINE}
            strokeWidth="1.6"
          />
          <path
            d="M30 19h36M30 27h22"
            stroke={LINE}
            strokeWidth="1.6"
            strokeLinecap="round"
            opacity="0.35"
          />
        </>
      )}
      {kind === "arrow" && (
        <path
          d="M6 24h48V12l36 20-36 20V40H6Z"
          fill="var(--spectrum-red)"
          stroke={LINE}
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

/** The canvas object: the art at the node's size, scaled by the shell. */
export function ReviewStickerWidget({ data }: WidgetNodeProps) {
  const d = data as Partial<ReviewStickerData> & { label?: string };
  return (
    <div style={{ width: REVIEW_STICKER_W * 2, height: REVIEW_STICKER_H * 2 }}>
      <ReviewStickerArt
        kind={d.kind ?? "badge"}
        word={d.word}
        field={d.field}
        size={d.size}
        title={d.label ?? d.word ?? "Sticker"}
      />
    </div>
  );
}

interface ReviewStickerSpec {
  key: string;
  name: string;
  meaning: string;
  data: ReviewStickerData;
}

/**
 * STICKER_ITEMS — eleven objects: two callouts for the user's own words, one
 * arrow that points, and the eight review words, one hue each so the board
 * reads at zoom-out. The callouts carry no editable text here: the product's
 * own speech and thought callouts are drawn stickers without an editing
 * path, so these port as drawn (D39 records the gap).
 */
const SPECS: ReviewStickerSpec[] = [
  {
    key: "callout-say",
    name: "Speech callout",
    meaning: "A sentence in your own words",
    data: { kind: "speech" },
  },
  {
    key: "callout-think",
    name: "Thought callout",
    meaning: "A thought in your own words",
    data: { kind: "thought" },
  },
  {
    key: "arrow-chunky",
    name: "Chunky arrow",
    meaning: "Points at the thing",
    data: { kind: "arrow" },
  },
  {
    key: "ship-it",
    name: "Ship it",
    meaning: "Ready to go",
    data: {
      kind: "badge",
      word: "Ship it",
      field: "var(--spectrum-green-light)",
    },
  },
  {
    key: "blocked",
    name: "Blocked",
    meaning: "Stopped until something changes",
    data: {
      kind: "badge",
      word: "Blocked",
      field: "var(--spectrum-red-light)",
    },
  },
  {
    key: "needs-review",
    name: "Needs review",
    meaning: "Asks for eyes",
    data: {
      kind: "badge",
      word: "Needs review",
      field: "var(--spectrum-orange-light)",
      size: 11,
    },
  },
  {
    key: "lgtm",
    name: "LGTM",
    meaning: "Approved",
    data: { kind: "badge", word: "LGTM", field: "var(--spectrum-blue-light)" },
  },
  {
    key: "wip",
    name: "WIP",
    meaning: "In flight",
    data: { kind: "badge", word: "WIP", field: "var(--spectrum-yellow-light)" },
  },
  {
    key: "owner",
    name: "Owner?",
    meaning: "A question of ownership",
    data: {
      kind: "badge",
      word: "Owner?",
      field: "var(--spectrum-violet-light)",
    },
  },
  {
    key: "nice",
    name: "Nice",
    meaning: "Praise",
    data: {
      kind: "badge",
      word: "Nice",
      field: "var(--spectrum-mulberry-light)",
    },
  },
  {
    key: "parking-lot",
    name: "Parking lot",
    meaning: "Deferred",
    data: {
      kind: "badge",
      word: "Parking lot",
      field: "var(--spectrum-indigo-light)",
    },
  },
];

// The picker's art fills whatever cell holds it at the sticker's 3:2 — the
// sheet's `.xtc-sticker-art` rule, "art fills its cell"; the placement size
// is the node's business (`defaultWidth` × `defaultHeight`).
const preview = (spec: ReviewStickerSpec) =>
  function ReviewStickerPreview() {
    return (
      <div style={{ width: "100%", aspectRatio: "3 / 2" }}>
        <ReviewStickerArt {...spec.data} title={spec.name} />
      </div>
    );
  };

export const REVIEW_STICKER_PRESETS: WidgetPreset[] = SPECS.map((spec) => ({
  id: `ds-sticker-${spec.key}`,
  label: spec.name,
  meaning: spec.meaning,
  family: "review",
  component: ReviewStickerWidget,
  preview: preview(spec),
  defaultWidth: REVIEW_STICKER_W * 2,
  defaultHeight: REVIEW_STICKER_H * 2,
  resizable: {
    minWidth: REVIEW_STICKER_W / 2,
    minHeight: REVIEW_STICKER_H / 2,
  },
  initialData: { ...spec.data },
}));
